import { useState } from 'react';
import { updateUserAccessPlan } from '../../api/adminService';
import '../../styles/auth.scss';

const PLAN_OPTIONS = [
  { key: '1month',   label: '1 Month',   desc: '30 days from now' },
  { key: '3months',  label: '3 Months',  desc: '90 days from now' },
  { key: '6months',  label: '6 Months',  desc: '180 days from now' },
  { key: '1year',    label: '1 Year',    desc: '365 days from now' },
  { key: 'lifetime', label: 'Lifetime',  desc: 'No expiry' },
  { key: 'custom',   label: 'Custom',    desc: 'Pick a date' },
];

const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');

const UserAccessPlanModal = ({ user, onClose, onSave }) => {
  const isAdmin = user.role === 'admin';
  const [plan, setPlan] = useState(user.accessPlan || '1month');
  const [customDate, setCustomDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const submit = async (planKey, extra = {}) => {
    setSaving(true);
    setMsg('');
    try {
      const { data } = await updateUserAccessPlan(user._id, { plan: planKey, ...extra });
      onSave?.(user._id, data.user);
      onClose();
    } catch (err) {
      setMsg(err?.response?.data?.message || 'Failed to update plan.');
    } finally {
      setSaving(false);
    }
  };

  const handleApply = () => {
    if (plan === 'custom') {
      if (!customDate) { setMsg('Pick an expiry date.'); return; }
      submit('custom', { expiresAt: new Date(customDate).toISOString() });
    } else {
      submit(plan);
    }
  };

  const handleRevoke = () => {
    if (!window.confirm('Revoke access immediately? User will be locked out.')) return;
    submit('revoke');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520, width: '95vw' }}>
        <div className="modal-header">
          <div>
            <h2>⏳ Access Plan</h2>
            <p style={{ marginTop: 4, fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)' }}>
              {user.name} &middot; {user.email}
            </p>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body" style={{ padding: '1rem 1.25rem' }}>
          {isAdmin && (
            <div style={{
              padding: '0.6rem 0.8rem', borderRadius: 8, marginBottom: '0.85rem',
              background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
              color: '#fca5a5', fontSize: '0.8rem',
            }}>
              Admin accounts are not restricted by access plans.
            </div>
          )}

          <div style={{
            background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 10, padding: '0.7rem 0.9rem', marginBottom: '1rem',
            fontSize: '0.8rem', color: 'rgba(255,255,255,0.8)',
          }}>
            <div><strong>Current plan:</strong> {user.accessPlan || '— none —'}</div>
            <div><strong>Granted:</strong> {fmt(user.accessGrantedAt)}</div>
            <div><strong>Expires:</strong> {user.accessPlan === 'lifetime' ? 'Never' : fmt(user.accessExpiresAt)}</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
            {PLAN_OPTIONS.map((opt) => {
              const active = plan === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setPlan(opt.key)}
                  disabled={isAdmin}
                  style={{
                    textAlign: 'left',
                    padding: '0.6rem 0.75rem',
                    borderRadius: 8,
                    cursor: isAdmin ? 'not-allowed' : 'pointer',
                    border: active ? '1px solid #667eea' : '1px solid rgba(255,255,255,0.1)',
                    background: active
                      ? 'linear-gradient(135deg, rgba(102,126,234,0.25), rgba(118,75,162,0.25))'
                      : 'rgba(255,255,255,0.03)',
                    color: '#fff',
                    opacity: isAdmin ? 0.5 : 1,
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{opt.label}</div>
                  <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>{opt.desc}</div>
                </button>
              );
            })}
          </div>

          {plan === 'custom' && !isAdmin && (
            <div style={{ marginTop: '0.75rem' }}>
              <label style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)' }}>Expiry date & time</label>
              <input
                type="datetime-local"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                className="search-input"
                style={{ width: '100%', marginTop: 4 }}
              />
            </div>
          )}

          {msg && (
            <div style={{
              marginTop: '0.75rem', padding: '0.5rem 0.7rem', borderRadius: 6,
              background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: '0.78rem',
            }}>{msg}</div>
          )}
        </div>

        <div className="modal-footer" style={{ display: 'flex', gap: '0.5rem', padding: '0.85rem 1.25rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <button
            type="button"
            onClick={handleRevoke}
            disabled={saving || isAdmin}
            className="btn-auth"
            style={{ background: 'linear-gradient(135deg,#ef4444,#dc2626)', flex: '0 0 auto' }}
          >
            🚫 Revoke Now
          </button>
          <div style={{ flex: 1 }} />
          <button type="button" onClick={onClose} className="btn-auth" style={{ background: 'rgba(255,255,255,0.1)' }}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={saving || isAdmin}
            className="btn-auth"
          >
            {saving ? 'Saving…' : 'Apply Plan'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserAccessPlanModal;
